// Helpers of the operations back office (Configurations → Operations). The server stores and sends the dates in
// UTC without a zone: they are shown as they are, labelled UTC, so the support reads the same value as the database.

export const KINDS = ['EfficiencyTestExecution', 'CertificateImport']
// The states a filter offers. `Running` is not among them: an execution is written when it ends, so no row is ever
// found running; what is still going on is not the back office's to show.
export const EXECUTION_STATES = ['Succeeded', 'Failed', 'Released']
export const PAGE_SIZE = 50

/** `2026-11-02T12:03:04.123` → `2026-11-02 12:03:04`; empty for a missing value. */
export function formatUtc(value) {
  if (!value) {
    return ''
  }
  return String(value).replace('T', ' ').replace(/Z$/, '').slice(0, 19)
}

/** `2026-11-02T12:03:04` → `2026-11-02`; empty for a missing value. */
export function formatDay(value) {
  return value ? String(value).slice(0, 10) : ''
}

function utcMillis(value) {
  const text = String(value)
  return Date.parse(/Z$|[+-]\d{2}:\d{2}$/.test(text) ? text : text + 'Z')
}

/** How long between two server dates, as `45s`, `3m 20s`, `2h 05m`; empty while the end is missing. */
export function durationOf(start, end) {
  if (!start || !end) {
    return ''
  }
  const seconds = Math.max(0, Math.round((utcMillis(end) - utcMillis(start)) / 1000))
  if (seconds < 60) {
    return `${seconds}s`
  }
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) {
    return `${minutes}m ${String(seconds % 60).padStart(2, '0')}s`
  }
  return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, '0')}m`
}

/** A JSON column as stored, indented for reading; the text as it is when it is not JSON. */
export function prettyJson(text) {
  if (!text) {
    return ''
  }
  try {
    return JSON.stringify(JSON.parse(text), null, 2)
  } catch (e) {
    return text
  }
}

function parseJson(text) {
  if (!text) {
    return null
  }
  try {
    return JSON.parse(text)
  } catch (e) {
    return null
  }
}

/**
 * The `steps` column, one entry per step in the order they ran: `{ step, startedAt, finishedAt, done, total, failed }`.
 * `null` when there is nothing to read as such (no history, or a text that is not a list of steps): the caller then
 * shows the text as it is.
 */
export function parseSteps(text) {
  const steps = parseJson(text)
  if (!Array.isArray(steps) || !steps.every((s) => s && typeof s.step === 'string')) {
    return null
  }
  return steps.map((s) => ({
    step: s.step,
    startedAt: s.startedAt || null,
    finishedAt: s.finishedAt || null,
    done: s.done === undefined ? null : s.done,
    total: s.total === undefined ? null : s.total,
    failed: s.failed || 0,
  }))
}

/**
 * The `context` column as `[key, value]` pairs, in the order stored. `null` when it is not a flat object (the caller
 * then shows the text as it is); a value that is itself an object is shown as JSON.
 */
export function parseContext(text) {
  const context = parseJson(text)
  if (!context || typeof context !== 'object' || Array.isArray(context)) {
    return null
  }
  return Object.keys(context).map((key) => {
    const value = context[key]
    return [key, value !== null && typeof value === 'object' ? JSON.stringify(value) : value]
  })
}

const PRODUCT_KEY_PREFIX = 'product:'
const ATTEMPT_KEY_PREFIX = 'attempt:'
const LOST_KEY_PREFIX = 'lost:'

/**
 * What an execution refers to, read from its row: an Efficiency Test (`test`), the product an import created
 * (`product`), the certificate of a failed import (`attempt`, from `attempt:{issuer code}|{identifier}`), an import
 * found lost whose certificate is not known (`lost`, from `lost:{operation id}`), or a key this page does not know
 * (`other`).
 * @returns {{ type: string, id: (number|null), name: (string|null), issuerCode: (string|null), identifier: (string|null), deleted: boolean }}
 */
export function describeSubject(execution) {
  const key = execution.subjectKey || ''
  const subject = { type: 'other', id: null, name: execution.subjectName || null, issuerCode: null, identifier: null }
  subject.deleted = !!execution.isSubjectDeleted

  if (execution.kind === 'EfficiencyTestExecution') {
    const id = execution.efficiencyTestId || (/^\d+$/.test(key) ? Number(key) : null)
    return { ...subject, type: 'test', id }
  }
  if (execution.productId || key.startsWith(PRODUCT_KEY_PREFIX)) {
    const fromKey = key.startsWith(PRODUCT_KEY_PREFIX) ? Number(key.slice(PRODUCT_KEY_PREFIX.length)) : null
    return { ...subject, type: 'product', id: execution.productId || fromKey }
  }
  if (key.startsWith(LOST_KEY_PREFIX)) {
    return { ...subject, type: 'lost' }
  }
  if (key.startsWith(ATTEMPT_KEY_PREFIX)) {
    const rest = key.slice(ATTEMPT_KEY_PREFIX.length)
    const separator = rest.indexOf('|')
    return separator < 0
      ? { ...subject, type: 'attempt', identifier: rest }
      : { ...subject, type: 'attempt', issuerCode: rest.slice(0, separator), identifier: rest.slice(separator + 1) }
  }
  return subject
}

/** The body of a search: blank filters are left out, so that the server reads them as "any". */
export function searchBody(filters, page) {
  const body = { page, size: PAGE_SIZE }
  Object.keys(filters).forEach((key) => {
    const value = filters[key]
    if (value !== null && value !== undefined && String(value).trim() !== '') {
      body[key] = typeof value === 'string' ? value.trim() : value
    }
  })
  return body
}
